'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@clerk/nextjs';
import { useIsAnonAuthed } from '@lib/auth/contexts/anon-auth-context';
import { useAuthShellPending } from '@lib/auth/contexts/auth-shell-pending-context';
import type { AuthedInfiniteQueryOptions, AuthedMutationOptions, AuthedQueryOptions } from './authed-query-meta-types';
import { sanitizeAuthedQueryMeta } from './sanitize-authed-query-meta';
import { type InfiniteData, type QueryKey, type UseInfiniteQueryOptions, type UseInfiniteQueryResult, type UseMutationResult, type UseQueryOptions, type UseQueryResult, useInfiniteQuery, useMutation, useQuery } from './tanstack-reexports';

/** True when an authed-gated query finished or was disabled before fetch. */
export function isAuthedGatedSettled(query: {
  isSuccess: boolean;
  isError: boolean;
  fetchStatus: string;
  status: string;
}): boolean {
  return query.isSuccess || query.isError || query.fetchStatus === 'idle' && query.status === 'pending';
}

/**
 * True once the (main) shell session is resolved and the viewer is a signed-in
 * Clerk org user (not anon/scoped). While the layout stream is pending, or
 * before `AuthSessionProvider` applies the seed, returns false.
 */
export function useAuthedGate() {
  const authShellPending = useAuthShellPending();
  const isAnonHint = useIsAnonAuthed();
  const {
    isLoaded,
    userId
  } = useAuth();
  if (authShellPending || isAnonHint === null) {
    return false;
  }
  if (isAnonHint === true) {
    return false;
  }
  return isLoaded && !!userId;
}
export function resolveAuthedEnabled<TQueryFnData, TError, TData, TQueryKey extends QueryKey>(enabled: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>['enabled'] | undefined, authedGate: boolean): UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>['enabled'] {
  if (enabled === undefined) return authedGate;
  if (typeof enabled === 'function') {
    return query => authedGate && enabled(query);
  }
  return enabled && authedGate;
}
export function useAuthedQuery(options) {
  const $ = _c(15);
  const authedGate = useAuthedGate();
  let enabled;
  let meta;
  let rest;
  if ($[0] !== options) {
    ({
      enabled,
      meta,
      ...rest
    } = options);
    $[0] = options;
    $[1] = enabled;
    $[2] = meta;
    $[3] = rest;
  } else {
    enabled = $[1];
    meta = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== authedGate || $[5] !== enabled) {
    t0 = resolveAuthedEnabled(enabled, authedGate);
    $[4] = authedGate;
    $[5] = enabled;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[7] !== meta) {
    t1 = sanitizeAuthedQueryMeta(meta);
    $[7] = meta;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = {
      expectedFailure: "forbidden",
      ...t1
    };
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== rest || $[12] !== t0 || $[13] !== t2) {
    t3 = {
      ...rest,
      enabled: t0,
      meta: t2
    };
    $[11] = rest;
    $[12] = t0;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return useQuery(t3);
}
export function useAuthedInfiniteQuery(options) {
  const $ = _c(15);
  const authedGate = useAuthedGate();
  let enabled;
  let meta;
  let rest;
  if ($[0] !== options) {
    ({
      enabled,
      meta,
      ...rest
    } = options);
    $[0] = options;
    $[1] = enabled;
    $[2] = meta;
    $[3] = rest;
  } else {
    enabled = $[1];
    meta = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== authedGate || $[5] !== enabled) {
    t0 = enabled === undefined ? authedGate : typeof enabled === "function" ? query => authedGate && enabled(query) : enabled && authedGate;
    $[4] = authedGate;
    $[5] = enabled;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const resolvedEnabled = t0;
  let t1;
  if ($[7] !== meta) {
    t1 = sanitizeAuthedQueryMeta(meta);
    $[7] = meta;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = {
      expectedFailure: "forbidden",
      ...t1
    };
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== resolvedEnabled || $[12] !== rest || $[13] !== t2) {
    t3 = {
      ...rest,
      enabled: resolvedEnabled,
      meta: t2
    };
    $[11] = resolvedEnabled;
    $[12] = rest;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return useInfiniteQuery(t3);
}
export function useAuthedMutation(options) {
  const $ = _c(10);
  let meta;
  let rest;
  if ($[0] !== options) {
    ({
      meta,
      ...rest
    } = options);
    $[0] = options;
    $[1] = meta;
    $[2] = rest;
  } else {
    meta = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== meta) {
    t0 = sanitizeAuthedQueryMeta(meta);
    $[3] = meta;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== t0) {
    t1 = {
      expectedFailure: "forbidden",
      ...t0
    };
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== rest || $[8] !== t1) {
    t2 = {
      ...rest,
      meta: t1
    };
    $[7] = rest;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
}